/**
 * Excel Upload Component
 * Bulk upload entities, signals, and rules via Excel
 */

import React, { useState, useRef } from 'react';
import useStore from '../store/useStore';
import sharePointService from '../services/sharePointService';
import excelParser from '../services/excelParser';
import './ExcelUpload.css';

const ExcelUpload = ({ onClose }) => {
  const [uploadType, setUploadType] = useState('entities');
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationResults, setValidationResults] = useState(null);
  const [uploadResults, setUploadResults] = useState(null);
  const fileInputRef = useRef(null);
  
  const { 
    selectedAsset, 
    entities,
    refreshAllData 
  } = useStore();

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.match(/\.(xlsx|xls)$/i)) {
      setFile(droppedFile);
      setValidationResults(null);
      setUploadResults(null);
    } else {
      alert('Please drop an Excel file (.xlsx or .xls)');
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setValidationResults(null);
      setUploadResults(null);
    }
  };

  const handleValidate = async () => {
    if (!file) {
      alert('Please select a file first');
      return;
    }

    if (!selectedAsset) {
      alert('Please select an asset first');
      return;
    }

    setIsProcessing(true);
    setValidationResults(null);

    try {
      // Parse Excel file
      const parsedData = await excelParser.parseExcelFile(file);
      
      // Get the appropriate sheet based on upload type
      let sheetData;
      const sheetNames = parsedData.sheetNames;
      
      if (sheetNames.includes(uploadType.charAt(0).toUpperCase() + uploadType.slice(1))) {
        sheetData = parsedData.sheets[uploadType.charAt(0).toUpperCase() + uploadType.slice(1)];
      } else if (sheetNames.length > 0) {
        sheetData = parsedData.sheets[sheetNames[0]];
      } else {
        throw new Error('No sheets found in Excel file');
      }

      if (!sheetData || sheetData.length === 0) {
        throw new Error('No data found in Excel sheet');
      }

      // Validate based on type
      let validation;
      
      if (uploadType === 'entities') {
        validation = excelParser.validateEntities(sheetData, selectedAsset.Id);
      } else if (uploadType === 'signals') {
        const entitiesMap = new Map();
        entities.forEach(entity => {
          entitiesMap.set(entity.Title, entity.Id);
        });
        validation = excelParser.validateSignals(sheetData, entitiesMap);
      } else if (uploadType === 'rules') {
        const entitiesMap = new Map();
        entities.forEach(entity => {
          entitiesMap.set(entity.Title, entity.Id);
        });
        validation = excelParser.validateRules(sheetData, entitiesMap);
      }

      setValidationResults({
        totalRows: sheetData.length,
        validRecords: validation.validRecords,
        errors: validation.errors
      });

    } catch (error) {
      console.error('Validation error:', error);
      alert('Failed to validate file: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpload = async () => {
    if (!validationResults || validationResults.validRecords.length === 0) {
      alert('No valid records to upload. Please validate first.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      let totalCreated = 0;
      let totalUpdated = 0;
      let totalErrors = 0;
      const uploadErrors = [];

      const { validRecords } = validationResults;
      const progressStep = 100 / validRecords.length;

      // Upload based on type
      if (uploadType === 'entities') {
        for (let i = 0; i < validRecords.length; i++) {
          try {
            await sharePointService.createEntity(validRecords[i]);
            totalCreated++;
          } catch (error) {
            console.error('Error creating entity:', error);
            totalErrors++;
            uploadErrors.push(`Row ${i + 2}: ${error.message}`);
          }
          setUploadProgress((i + 1) * progressStep);
        }
      } else if (uploadType === 'signals') {
        for (let i = 0; i < validRecords.length; i++) {
          try {
            // Check if signal exists
            const existingSignal = await sharePointService.checkDuplicateSignal(
              validRecords[i].EntityIdLookupId,
              validRecords[i].SignalType,
              validRecords[i].Timestamp
            );
            
            // Create or update
            await sharePointService.createSignal(validRecords[i]);
            
            if (existingSignal) {
              totalUpdated++;
            } else {
              totalCreated++;
            }
          } catch (error) {
            console.error('Error creating/updating signal:', error);
            totalErrors++;
            uploadErrors.push(`Row ${i + 2}: ${error.message}`);
          }
          setUploadProgress((i + 1) * progressStep);
        }
      } else if (uploadType === 'rules') {
        for (let i = 0; i < validRecords.length; i++) {
          try {
            await sharePointService.createRule(validRecords[i]);
            totalCreated++;
          } catch (error) {
            console.error('Error creating rule:', error);
            totalErrors++;
            uploadErrors.push(`Row ${i + 2}: ${error.message}`);
          }
          setUploadProgress((i + 1) * progressStep);
        }
      }

      setUploadProgress(100);

      // Set results
      setUploadResults({
        created: totalCreated,
        updated: totalUpdated,
        failed: totalErrors,
        errors: uploadErrors
      });

      // Show summary
      const successParts = [];
      if (totalCreated > 0) successParts.push(`${totalCreated} created`);
      if (totalUpdated > 0) successParts.push(`${totalUpdated} updated`);
      if (totalErrors > 0) successParts.push(`${totalErrors} failed`);

      alert(
        `Upload complete!\n${successParts.join(', ')}\n\nRefreshing data...`
      );

      // Refresh data
      await refreshAllData();

      // Close modal after successful upload
      if (totalErrors === 0) {
        onClose();
      }

    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const downloadTemplate = () => {
    let workbook;
    let filename;

    switch (uploadType) {
      case 'entities':
        workbook = excelParser.generateEntitiesTemplate();
        filename = 'Entities_Template.xlsx';
        break;
      case 'signals':
        workbook = excelParser.generateSignalsTemplate();
        filename = 'Signals_Template.xlsx';
        break;
      case 'rules':
        workbook = excelParser.generateRulesTemplate();
        filename = 'Rules_Template.xlsx';
        break;
      default:
        return;
    }

    excelParser.downloadWorkbook(workbook, filename);
  };

  const resetUpload = () => {
    setFile(null);
    setValidationResults(null);
    setUploadResults(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content excel-upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📊 Excel Bulk Upload</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {/* Upload Type Selection */}
          <div className="upload-type-selector">
            <h3>What do you want to upload?</h3>
            <div className="upload-type-buttons">
              <button 
                className={`type-btn ${uploadType === 'entities' ? 'active' : ''}`}
                onClick={() => {
                  setUploadType('entities');
                  resetUpload();
                }}
                disabled={isProcessing || isUploading}
              >
                <span className="type-icon">📝</span>
                <span className="type-label">Entities</span>
                <span className="type-desc">Metrics, CSFs, Projects</span>
              </button>
              <button 
                className={`type-btn ${uploadType === 'signals' ? 'active' : ''}`}
                onClick={() => {
                  setUploadType('signals');
                  resetUpload();
                }}
                disabled={isProcessing || isUploading}
              >
                <span className="type-icon">📈</span>
                <span className="type-label">Signals</span>
                <span className="type-desc">Production data, Scores</span>
              </button>
              <button 
                className={`type-btn ${uploadType === 'rules' ? 'active' : ''}`}
                onClick={() => {
                  setUploadType('rules');
                  resetUpload();
                }}
                disabled={isProcessing || isUploading}
              >
                <span className="type-icon">🔗</span>
                <span className="type-label">Rules</span>
                <span className="type-desc">Dependencies</span>
              </button>
            </div>
          </div>

          {/* Template Download */}
          <div className="template-section">
            <button 
              className="btn-download-template" 
              onClick={downloadTemplate}
              disabled={isProcessing || isUploading}
            >
              ⬇️ Download Excel Template
            </button>
            <p className="template-note">
              Download the template, fill in your data, then upload it below
            </p>
          </div>

          {/* File Upload Area */}
          <div 
            className={`file-drop-zone ${isDragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !isProcessing && !isUploading && fileInputRef.current.click()}
          >
            <input 
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              disabled={isProcessing || isUploading}
            />
            
            {file ? (
              <div className="file-selected">
                <span className="file-icon">📄</span>
                <div className="file-info">
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                </div>
                {!isProcessing && !isUploading && (
                  <button 
                    className="btn-remove-file"
                    onClick={(e) => {
                      e.stopPropagation();
                      resetUpload();
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            ) : (
              <div className="file-prompt">
                <span className="drop-icon">📂</span>
                <p className="drop-text">Drag & drop Excel file here</p>
                <p className="drop-subtext">or click to browse</p>
                <p className="drop-format">.xlsx or .xls files only</p>
              </div>
            )}
          </div>

          {/* Validation Results */}
          {validationResults && (
            <div className="validation-results">
              <h4>📋 Validation Results</h4>
              <div className="validation-summary">
                <div className="validation-stat">
                  <span className="stat-icon">📊</span>
                  <span className="stat-value">{validationResults.totalRows}</span>
                  <span className="stat-label">Total Rows</span>
                </div>
                <div className="validation-stat success">
                  <span className="stat-icon">✅</span>
                  <span className="stat-value">{validationResults.validRecords.length}</span>
                  <span className="stat-label">Valid</span>
                </div>
                <div className="validation-stat error">
                  <span className="stat-icon">❌</span>
                  <span className="stat-value">{validationResults.errors.length}</span>
                  <span className="stat-label">Errors</span>
                </div>
              </div>

              {validationResults.errors.length > 0 && (
                <div className="error-list">
                  <h5>⚠️ Validation Errors:</h5>
                  <div className="error-scroll">
                    <ul>
                      {validationResults.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {validationResults.validRecords.length > 0 && (
                <p className="validation-note">
                  ✓ {validationResults.validRecords.length} record{validationResults.validRecords.length !== 1 ? 's' : ''} ready to upload
                </p>
              )}
            </div>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <div className="upload-progress">
              <div className="progress-header">
                <span>⏳ Uploading data...</span>
                <span>{Math.round(uploadProgress)}%</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Upload Results */}
          {uploadResults && (
            <div className="upload-results">
              <h4>✅ Upload Complete</h4>
              <div className="results-summary">
                {uploadResults.created > 0 && (
                  <div className="result-stat success">
                    <span className="stat-icon">➕</span>
                    <span className="stat-value">{uploadResults.created}</span>
                    <span className="stat-label">Created</span>
                  </div>
                )}
                {uploadResults.updated > 0 && (
                  <div className="result-stat updated">
                    <span className="stat-icon">🔄</span>
                    <span className="stat-value">{uploadResults.updated}</span>
                    <span className="stat-label">Updated</span>
                  </div>
                )}
                {uploadResults.failed > 0 && (
                  <div className="result-stat error">
                    <span className="stat-icon">❌</span>
                    <span className="stat-value">{uploadResults.failed}</span>
                    <span className="stat-label">Failed</span>
                  </div>
                )}
              </div>

              {uploadResults.errors && uploadResults.errors.length > 0 && (
                <div className="error-list">
                  <h5>⚠️ Upload Errors:</h5>
                  <div className="error-scroll">
                    <ul>
                      {uploadResults.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="btn-secondary" 
            onClick={onClose}
            disabled={isProcessing || isUploading}
          >
            {uploadResults ? 'Close' : 'Cancel'}
          </button>
          
          {!validationResults && (
            <button 
              className="btn-primary" 
              onClick={handleValidate}
              disabled={!file || isProcessing || isUploading}
            >
              {isProcessing ? 'Validating...' : '🔍 Validate Data'}
            </button>
          )}

          {validationResults && !uploadResults && (
            <button 
              className="btn-primary" 
              onClick={handleUpload}
              disabled={validationResults.validRecords.length === 0 || isUploading}
            >
              {isUploading ? 'Uploading...' : `📤 Upload ${validationResults.validRecords.length} Record${validationResults.validRecords.length !== 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExcelUpload;